/**
 * Small hook that returns which CMSs the current user can access.
 * Consumed by:
 *   - Navbar `CmsMenu` (top-nav CMS ▾ dropdown, only appears if at least
 *     one role is present)
 *   - CmsSwitcherBar (slim in-CMS bar rendered inside AdminHome + UniPortal
 *     for fast in-flow switching)
 *
 * Uses two live endpoints:
 *   GET /api/admin/whoami  → 200 if the current user is a platform admin,
 *                            403 otherwise. We swallow the 403.
 *   GET /api/claims/mine   → returns the list of school claims (approved +
 *                            pending). We only show the switcher for
 *                            approved ones.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const _CACHE_KEY = "wua:cms-roles:v2";

export function useCmsRoles() {
  const { user } = useAuth();
  const [state, setState] = useState(() => {
    try {
      const raw = sessionStorage.getItem(_CACHE_KEY);
      return raw ? JSON.parse(raw) : { isAdmin: false, schools: [], firms: [], loaded: false };
    } catch (e) {
      return { isAdmin: false, schools: [], firms: [], loaded: false };
    }
  });

  useEffect(() => {
    if (!user) {
      setState({ isAdmin: false, schools: [], firms: [], loaded: true });
      return;
    }
    let cancelled = false;
    (async () => {
      const [adminRes, claimsRes, firmsRes] = await Promise.allSettled([
        api.get("/admin/whoami"),
        api.get("/claims/mine"),
        api.get("/firm-portal/mine"),
      ]);
      const isAdmin = adminRes.status === "fulfilled" && adminRes.value?.status === 200;
      const claims = claimsRes.status === "fulfilled" ? (claimsRes.value?.data?.items || []) : [];
      const schools = claims
        .filter((c) => c.status === "approved")
        .map((c) => ({ id: c.university_id, name: c.university_name || c.university_id }));
      const firms = firmsRes.status === "fulfilled"
        ? (firmsRes.value?.data?.firms || []).map((f) => ({
            slug: f.slug,
            name: f.firm_name || f.slug,
            tier: f.tier || "claimed",
          }))
        : [];
      const next = { isAdmin, schools, firms, loaded: true };
      if (!cancelled) {
        setState(next);
        try {
          sessionStorage.setItem(_CACHE_KEY, JSON.stringify(next));
        } catch (e) {
          /* quota — ignore */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return state;
}
